import { BottomSheet, Badge, EmptyState, Icon, useMediaQuery } from "@helmr/ui";
import type { LeverId } from "@helmr/contracts";
import { useEffect, useId, useState, type ReactNode } from "react";
import type { GroupNode, LeverNode, LeverTree } from "../../api/roles";
import { PHONE_QUERY } from "../../app/mobile/query";
import { filterTree, groupCount, groupOf, groupState, isNewLever, lockedIn, newLeversIn, setLever, startsExpanded, toggleGroup, turnOnNew, unmetNeeds, type CanGrant, type GroupState } from "./rolesTree";
import s from "./roles.module.css";

const CANT = "You don't have this";

/** One lever: assigned or not. */
export function LeverSwitch({ on, label, disabled, onChange }: { on: boolean; label: string; disabled?: boolean; onChange: (on: boolean) => void }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} className={s.sw} data-on={on || undefined} onClick={() => onChange(!on)}>
      <i aria-hidden="true" />
    </button>
  );
}

const STATE_WORD: Record<GroupState, string> = { on: "all on", off: "all off", partial: "some on" };

/** A group's master. Three states: all on, all off, and partial (the thumb sits in the middle). It is a checkbox because only a checkbox may be "mixed". */
export function GroupSwitch({ state, label, disabled, onClick }: { state: GroupState; label: string; disabled?: boolean; onClick: () => void }) {
  return (
    <button type="button" role="checkbox" aria-checked={state === "partial" ? "mixed" : state === "on"} aria-label={`${label}: all levers (${STATE_WORD[state]})`}
      disabled={disabled} className={`${s.sw} ${s.master}`} data-state={state} onClick={(e) => { e.stopPropagation(); onClick(); }}>
      <i aria-hidden="true" />
    </button>
  );
}

export interface RoleTreeProps {
  tree: LeverTree;
  held: ReadonlySet<LeverId>;
  onChange: (next: Set<LeverId>) => void;
  canGrant: CanGrant;
  /** `role.leverRegistryVersion ?? 1`, or the tree's own version for a role being created (nothing is new there). */
  roleVersion: number;
  /** Owner role or your own role: the tree shows the state and changes nothing. */
  readOnly: boolean;
  /** Extra content under a lever row, e.g. the discount cap under `billing.discount`. */
  below?: (lever: LeverNode, on: boolean) => ReactNode;
}

export function RoleTree({ tree, held, onChange, canGrant, roleVersion, readOnly, below }: RoleTreeProps) {
  const phone = useMediaQuery(PHONE_QUERY);
  const [query, setQuery] = useState("");
  // Partial groups start open: they are the ones somebody came to adjust. After that the person decides.
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set(tree.groups.filter((g) => startsExpanded(g, held)).map((g) => g.id)));
  const [sheet, setSheet] = useState<string | null>(null);
  const [jump, setJump] = useState<string | null>(null);
  const searchId = useId();
  const searching = query.trim() !== "";
  const groups = filterTree(tree, query);
  const locked = (id: LeverId) => readOnly || !canGrant(id);

  // "Needs: X" scrolls to X: make sure its group is showing first, then move focus once it is on screen.
  useEffect(() => {
    if (!jump) return;
    const el = document.getElementById(`lever-${jump}`);
    if (el) { el.scrollIntoView?.({ block: "center" }); el.querySelector<HTMLElement>("[role=switch]")?.focus({ preventScroll: true }); setJump(null); }
  }, [jump, open, sheet, query]);
  const goTo = (id: string) => {
    const g = groupOf(tree, id);
    if (!g) return;
    setQuery("");
    if (phone) setSheet(g.id); else setOpen((o) => new Set(o).add(g.id));
    setJump(id);
  };

  const row = (l: LeverNode) => {
    const on = held.has(l.id);
    const needs = unmetNeeds(l, held, tree);
    const cant = !readOnly && !canGrant(l.id);
    return (
      <li key={l.id} id={`lever-${l.id}`} className={s.lever} data-on={on || undefined}>
        <div className={s.leverText}>
          <span className={s.leverLabel}>
            <span title={l.label}>{l.label}</span>
            {isNewLever(l, roleVersion) ? <Badge tone="info">New</Badge> : null}
          </span>
          <span className={s.leverHint}>{l.hint}</span>
          {cant ? <span className={s.cant}>{CANT}</span> : null}
          {needs.length ? (
            <span className={s.needs}>Needs:{" "}
              {needs.map((n, i) => <span key={n.id}>{i ? ", " : ""}<button type="button" className={s.needBtn} onClick={() => goTo(n.id)}>{n.label}</button></span>)}
            </span>
          ) : null}
          {below?.(l, on)}
        </div>
        <LeverSwitch on={on} label={l.label} disabled={locked(l.id)} onChange={(v) => onChange(setLever(held, l.id, v))} />
      </li>
    );
  };

  const head = (g: GroupNode, trailing: ReactNode) => {
    const { on, total } = groupCount(g, held);
    const state = groupState(g, held);
    const cant = lockedIn(g, canGrant);
    const fresh = newLeversIn(g, roleVersion);
    const allLocked = cant.length === g.levers.length;
    return (
      <div className={s.ghead}>
        <div className={s.gtext}>
          <span className={s.gname} title={g.label}>{g.label}</span>
          <span className={s.gmeta}>
            <span>{on} of {total}</span>
            {fresh.length ? <Badge tone="info">New · {fresh.length}</Badge> : null}
            {!readOnly && cant.length && !allLocked ? <span>{cant.length} you can&apos;t grant</span> : null}
          </span>
        </div>
        <GroupSwitch state={state} label={g.label} disabled={readOnly || allLocked} onClick={() => onChange(toggleGroup(g, held, canGrant))} />
        {trailing}
      </div>
    );
  };

  const newButton = (g: GroupNode) => {
    const grantable = newLeversIn(g, roleVersion).filter((l) => canGrant(l.id) && !held.has(l.id));
    if (readOnly || grantable.length === 0) return null;
    return <button type="button" className={s.turnOnNew} onClick={() => onChange(turnOnNew(g, held, roleVersion, canGrant))}>Turn on {grantable.length} new {grantable.length === 1 ? "lever" : "levers"}</button>;
  };

  const sheetGroup = tree.groups.find((g) => g.id === sheet) ?? null;

  return (
    <div className={s.tree}>
      <div className={s.search}>
        <label htmlFor={searchId} className={s.srOnly}>Search levers</label>
        <Icon name="search" size={14} />
        <input id={searchId} type="search" placeholder="Search levers" value={query} onChange={(e) => setQuery(e.target.value)} autoComplete="off" />
      </div>

      {tree.groups.length === 0 ? (
        <EmptyState compact icon="badge" title="No levers yet" description="The server returned an empty permission list. Try again in a moment." />
      ) : groups.length === 0 ? (
        <EmptyState compact icon="search" title="No lever matches" description={`Nothing is called "${query.trim()}". Try a shorter word.`} />
      ) : searching ? (
        // Search results are the same rows, flat under their group's name, on both layouts.
        <div className={s.groups}>
          {groups.map((g) => (
            <section key={g.id} className={s.group} aria-label={g.label}>
              {/* The row and its master stand for the whole group, not just the levers that match. */}
              {head(tree.groups.find((x) => x.id === g.id) ?? g, null)}
              <ul className={s.levers}>{g.levers.map(row)}</ul>
            </section>
          ))}
        </div>
      ) : phone ? (
        <div className={s.groups}>
          {groups.map((g) => (
            <section key={g.id} className={s.group} aria-label={g.label}>
              <div className={s.gopen} role="button" tabIndex={0} aria-label={`Open ${g.label}`} onClick={() => setSheet(g.id)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSheet(g.id); } }}>
                {head(g, <Icon name="back" size={14} className={s.go} />)}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className={s.groups}>
          {groups.map((g) => {
            const isOpen = open.has(g.id);
            return (
              <section key={g.id} className={s.group} aria-label={g.label}>
                <div className={s.gbar}>
                  {head(g, null)}
                  <button type="button" className={s.chev} aria-expanded={isOpen} aria-label={`${isOpen ? "Collapse" : "Expand"} ${g.label}`}
                    onClick={() => setOpen((o) => { const n = new Set(o); isOpen ? n.delete(g.id) : n.add(g.id); return n; })}>
                    <Icon name={isOpen ? "up" : "dn"} size={14} />
                  </button>
                </div>
                {isOpen ? <div>{newButton(g)}<ul className={s.levers}>{g.levers.map(row)}</ul></div> : null}
              </section>
            );
          })}
        </div>
      )}

      <BottomSheet open={phone && sheetGroup !== null} onOpenChange={(o) => { if (!o) setSheet(null); }} title={sheetGroup?.label ?? ""} description={sheetGroup ? `${groupCount(sheetGroup, held).on} of ${sheetGroup.levers.length} levers on` : undefined}
        footer={sheetGroup ? <button type="button" className={s.done} onClick={() => setSheet(null)}>Done</button> : undefined}>
        {sheetGroup ? (
          <div className={s.sheetBody}>
            <div className={s.sheetMaster}>
              <span>All {sheetGroup.label.toLowerCase()} levers</span>
              <GroupSwitch state={groupState(sheetGroup, held)} label={sheetGroup.label} disabled={readOnly || lockedIn(sheetGroup, canGrant).length === sheetGroup.levers.length} onClick={() => onChange(toggleGroup(sheetGroup, held, canGrant))} />
            </div>
            {newButton(sheetGroup)}
            <ul className={s.levers}>{sheetGroup.levers.map(row)}</ul>
          </div>
        ) : null}
      </BottomSheet>
    </div>
  );
}
